import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	resolvedTheme: 'light' | 'dark';
}

// Function to get system preference
const getSystemTheme = (): 'light' | 'dark' => {
	if (typeof window === 'undefined') return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Function to resolve theme (system or user preference)
const resolveTheme = (theme: Theme): 'light' | 'dark' => {
	if (theme === 'system') {
		return getSystemTheme();
	}
	return theme;
};

// Function to apply theme to document
const applyTheme = (theme: 'light' | 'dark') => {
	if (typeof window === 'undefined') return;

	const root = window.document.documentElement;

	if (theme === 'dark') {
		root.classList.add('dark');
	} else {
		root.classList.remove('dark');
	}
};

const useThemeStore = create<ThemeState>()(
	persist(
		set => {
			// Listen for system theme changes
			if (typeof window !== 'undefined') {
				window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
					set(state => {
						if (state.theme === 'system') {
							const newResolvedTheme = e.matches ? 'dark' : 'light';
							applyTheme(newResolvedTheme);
							return { resolvedTheme: newResolvedTheme };
						}
						return state;
					});
				});
			}

			return {
				theme: 'system', // Default to system preference
				resolvedTheme: getSystemTheme(),
				setTheme: (theme: Theme) => {
					const resolved = resolveTheme(theme);
					applyTheme(resolved);
					set({ theme, resolvedTheme: resolved });
				},
			};
		},
		{
			name: 'theme-storage',
			onRehydrateStorage: () => state => {
				// Apply theme on page load
				if (state) {
					applyTheme(state.resolvedTheme);
				}
			},
		},
	),
);

// Initialize theme on first load
if (typeof window !== 'undefined') {
	const initialTheme = useThemeStore.getState().theme;
	const resolved = resolveTheme(initialTheme);
	applyTheme(resolved);
}

export default useThemeStore;
